<script lang="ts">
  import { ShoppingCart } from "lucide-svelte";
  import { fakeProjectPrices } from "$src/domain/projects/items/FakeProjectDetail";
  import type { ProjectPrices } from "$src/domain/projects/ProjectDetail";
  import { Button } from "$lib/components/ui/button";
  import { Eye, EyeOff } from "lucide-svelte";
  import {
    isEditActive,
    setEditActive,
  } from "$src/application/main/mainTemplateStore";
  import * as Tooltip from "$lib/components/ui/tooltip/index.js";
  import { t } from "svelte-i18n";
  import { Trash2 } from "lucide-svelte";
  import { formatCurrency } from "$src/presentation/core/currencyUtils";
  import ProjectTemplate from "../../components/project-template.svelte";
  import { useDeleteDraft } from "../hooks/useDeleteDraft";
  import { usePublishDraft } from "../hooks/usePublishDraft";
  import PriceCard from "./amounts/price-card.svelte";
  import DeliveryCard from "./delivery/delivery-card.svelte";
  import EditProjectButton from "./edit-project-button.svelte";
  import LineItemDatatable from "./line-items/line-item-datatable.svelte";
  import { useProject } from "../../hooks/useProject";
  import ProjectLoading from "./project-loading.svelte";
  import ProjectError from "./project-error.svelte";
  import InfoCard from "./infos/InfoCard.svelte";
  import { usePatchDraft } from "../hooks/usePatchDraft";
  import * as jsonpatch from "fast-json-patch";
  import { debounce } from "$src/application/main/debounce";
  import { get } from "svelte/store";
  import {
    clearProjectFormErrors,
    createProjectSchedule,
    hasProjectFormErrors,
    projectDraft,
    projectDraftObserver,
    projectOriginal,
  } from "$src/application/projects/stores/projectStore";
  import AutoDraftSavingIndicator from "./AutoDraftSavingIndicator.svelte";
  import PriceBottomInfo from "./amounts/price-bottom-info.svelte";
  import * as AlertDialog from "$lib/components/ui/alert-dialog/index.js";
  import { type StoreSummary } from "$src/domain/directory/stores/StoreSummary";
  import PrintProjectDialog from "../../actions/print/print-project-dialog.svelte";
  import MailProjectDialog from "../../actions/mail/mail-project-dialog.svelte";
  import CopyProjectDialog from "../../actions/copy/copy-project-dialog.svelte";
  import OrderProjectDialog from "./actions/order-project-dialog.svelte";
  import ProjectErrorsAlert from "./project-errors-alert.svelte";
  import { useOrderProject } from "../../hooks/useOrderProject";
  import NextActionButton from "./actions/next-action-button.svelte";
  import PriceBottomEditInfo from "./amounts/price-bottom-edit-info.svelte";
  import { fly } from "svelte/transition";
  import CustomerInfoCard from "./infos/CustomerInfoCard.svelte";

  export let groupStoreId: number;
  export let projectId: number;
  export let stores: StoreSummary[];

  let marginVisible: boolean = false;

  let projectPrices: ProjectPrices = fakeProjectPrices;

  $: projectQuery = useProject(groupStoreId, projectId);

  $: {
    if ($projectQuery.isSuccess) {
      projectOriginal.set($projectQuery.data);
    }
  }

  const patchDraftCommand = usePatchDraft();
  const publishDraftCommand = usePublishDraft();
  const deleteDraftCommand = useDeleteDraft();
  const orderProjectCommand = useOrderProject();

  function switchMarginVisibility() {
    marginVisible = !marginVisible;
  }

  const debouncedPatchDraft = debounce(patchDraft, 2500);

  function patchDraft() {
    const operations = jsonpatch.generate(get(projectDraftObserver));

    get(patchDraftCommand).mutate({ groupStoreId, projectId, operations });
  }

  function publishDraftDemand() {
    if (hasProjectFormErrors()) {
      displayFormErrorsAlert(
        $t("page.projects.detail.errors-description"),
        true
      );
    } else {
      publishDraft();
    }
  }

  async function publishDraft() {
    displayCancelAlert = false;
    debouncedPatchDraft.cancel();

    //send not saved patch
    const operations = jsonpatch.generate(get(projectDraftObserver));
    if (operations.length > 0) {
      await $patchDraftCommand.mutateAsync({
        groupStoreId,
        projectId,
        operations,
      });
    }

    //do publish
    await $publishDraftCommand.mutateAsync({ groupStoreId, projectId });

    setEditActive(false);

    clearProjectFormErrors();
  }

  async function orderProject() {
    await publishDraft();
    await $orderProjectCommand.mutateAsync({ groupStoreId, projectId });
  }

  let allowContinueAction: boolean = false;
  let isProjectErrorsAlertVisible: boolean = false;
  let formErrorsDescription: string | undefined = undefined;

  function displayFormErrorsAlert(
    description: string | undefined,
    allowContinue: boolean
  ) {
    isProjectErrorsAlertVisible = true;
    formErrorsDescription = description;
    allowContinueAction = allowContinue;
  }

  function publishAnyway() {
    cancelFormErrorsAlert();
    publishDraft();
  }

  function cancelFormErrorsAlert() {
    isProjectErrorsAlertVisible = false;
  }

  let displayCancelAlert: boolean = false;

  function cancelAlert() {
    displayCancelAlert = true;
  }

  function cancelDraft() {
    displayCancelAlert = false;
    debouncedPatchDraft.cancel();

    setEditActive(false);
    get(deleteDraftCommand).mutate({ groupStoreId, projectId });
  }

  let displayOrderDialog: boolean = false;
  function orderProjectDemand() {
    if (hasProjectFormErrors()) {
      allowContinueAction = false;
      displayFormErrorsAlert(undefined, false);
    } else {
      createProjectSchedule($projectDraft);
      displayOrderDialog = true;
    }
  }
</script>

{#if $projectQuery.isSuccess}
  <ProjectTemplate isEmbedded={false}>
    <div class="flex-1">
      <div
        class="p-1 md:container flex-1 items-start md:grid md:gap-6 lg:gap-100"
      >
        <main class="relative xl:grid">
          <div class="mx-auto w-full min-w-0">
            <div class="flex flex-col gap-4">
              <div class="flex flex-wrap lg:flex-nowrap gap-2">
                <!-- <div class="flex gap-2 flex-col md:flex-row"> -->
                <CustomerInfoCard
                  {groupStoreId}
                  patchHandler={debouncedPatchDraft}
                />
                <InfoCard
                  {groupStoreId}
                  patchHandler={debouncedPatchDraft}
                  {stores}
                />
                <!-- </div> -->
                <DeliveryCard
                  {groupStoreId}
                  patchHandler={debouncedPatchDraft}
                />
              </div>
              <PriceCard {groupStoreId} {marginVisible} />
              <LineItemDatatable {marginVisible} />
            </div>

            <ul class="space-y-2">
              {#each Array(50).fill("Item") as item, i}
                <li class="bg-background p-2">{item} {i + 1}</li>
              {/each}
            </ul>
          </div>
        </main>
      </div>
    </div>

    <div slot="menu-top-right-action">
      <div class="gap-0 flex flex-row items-center justify-center">
        <!-- <p class="hidden sm:flex font-semibold mr-4">16000 €</p> -->

        <Tooltip.Root>
          <Tooltip.Trigger asChild let:builder>
            <Button
              builders={[builder]}
              on:click={switchMarginVisibility}
              variant="secondary"
              size="sm"
            >
              {#if marginVisible}
                <EyeOff class="h-4 w-4" />
              {:else}
                <Eye class="h-4 w-4" />
              {/if}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <p>{$t("page.projects.detail.displayMargin")}</p>
          </Tooltip.Content>
        </Tooltip.Root>

        <CopyProjectDialog />
        <MailProjectDialog />
        <PrintProjectDialog />
      </div>
    </div>

    <div slot="menu-info-bottom" class="flex flex-row justify-between h-12">
      <PriceBottomInfo {marginVisible} />
    </div>

    {#if $isEditActive}
      <div
        class=" hidden md:flex fixed bottom-14 w-full right-24 flex-row justify-between h-12"
        transition:fly={{ y: 100, duration: 300 }}
      >
        <PriceBottomEditInfo {marginVisible} />
      </div>
    {/if}

    <div
      slot="menu-action-bottom"
      class="border-t-2 p-1 md:p-3 flex flex-row gap-4 justify-between items-center h-14"
    >
      {#if $isEditActive}
        <div class="flex-shrink flex gap-2">
          <Button variant="destructive" class="gap-2">
            <Trash2 class="h-4 w-4" />
            <div class="hidden lg:flex">Supprimer</div>
          </Button>
        </div>
        <div class="flex-shrink flex gap-2">
          <NextActionButton {orderProjectDemand} />

          <OrderProjectDialog
            {groupStoreId}
            bind:open={displayOrderDialog}
            onConfirmOrder={orderProject}
          />
          <Button
            variant="secondary"
            on:click={publishDraftDemand}
            class="gap-2"
          >
            <AutoDraftSavingIndicator
              isSaving={$patchDraftCommand.isLoading}
              isError={$patchDraftCommand.isError}
            />
            {$t("global.messages.save")}
          </Button>
          <Button on:click={cancelAlert}>
            {$t("global.messages.cancel")}
          </Button>
        </div>
      {/if}
    </div>
    <EditProjectButton {groupStoreId} {projectId} />
  </ProjectTemplate>

  <AlertDialog.Root bind:open={displayCancelAlert}>
    <AlertDialog.Content>
      <AlertDialog.Header>
        <AlertDialog.Title>
          {$t("global.messages.confirm-cancel")}
        </AlertDialog.Title>
        <AlertDialog.Description>
          {$t("page.projects.detail.cancelDraftWarning")}
        </AlertDialog.Description>
      </AlertDialog.Header>
      <AlertDialog.Footer>
        <AlertDialog.Action on:click={cancelDraft}>
          {$t("global.messages.deleteDraft")}
        </AlertDialog.Action>
        <AlertDialog.Cancel>
          {$t("global.messages.continueEditing")}
        </AlertDialog.Cancel>
      </AlertDialog.Footer>
    </AlertDialog.Content>
  </AlertDialog.Root>

  <ProjectErrorsAlert
    bind:open={isProjectErrorsAlertVisible}
    bind:allowContinueAction
    bind:description={formErrorsDescription}
    onContinueAction={publishAnyway}
  />
{:else if $projectQuery.isError}
  {console.log($projectQuery.error)}
  <ProjectError error={$projectQuery.error} />
{:else if $projectQuery.isLoading}
  <ProjectLoading />
{/if}
